/**
 * Supported-viewport gate.
 *
 * The game renders at a fixed logical 1280x720 canvas under `Phaser.Scale.FIT`, and
 * `#game-container` is capped at 1280 CSS px wide. The canvas therefore never scales
 * *up*: CSS scale = containerWidth / GAME_WIDTH, which is always <= 1. Below a certain
 * container width every interactive target shrinks with it — at a 390px-wide phone the
 * scale is ~0.29, turning a 40px button into ~12 CSS px.
 *
 * Rather than silently shrinking the desktop UI to unusable sizes, we declare a minimum
 * supported viewport and show an explicit gate below it. See docs/UI_VIEWPORT_POLICY.md.
 *
 * Scale is measured from the DOM container, never from Phaser's ScaleManager: the game
 * runs with `zoom: devicePixelRatio`, so Phaser's reported display size is scaled by DPR
 * and is not a CSS-pixel quantity.
 */
import { GAME_WIDTH, GAME_HEIGHT } from './config/game.config';
import { isPhoneCompositionSupported, resolveResponsiveViewport } from './responsiveViewport';
import { SCENE_KEYS, type SceneKey } from './navigation/sceneKeys';
import { SCENE_REGISTRY } from './navigation/sceneRegistry';

/**
 * Smallest height, in logical canvas px, that any interactive control may have.
 *
 * This is an invariant the UI must uphold, not a measurement of it: a control shorter than
 * this renders below MIN_TARGET_CSS_PX at the supported floor, silently falsifying the
 * policy. Matches the button defaults in campaignV2UiPrimitives and StagePrepScene.
 * Lowering it raises the minimum supported viewport. See docs/UI_VIEWPORT_POLICY.md.
 */
export const MIN_CONTROL_LOGICAL_PX = 40;

/** WCAG 2.2 SC 2.5.8 Target Size (Minimum), Level AA. */
export const MIN_TARGET_CSS_PX = 24;

/** Canvas scale below which our smallest control drops under the AA target-size floor. */
export const MIN_SUPPORTED_SCALE = MIN_TARGET_CSS_PX / MIN_CONTROL_LOGICAL_PX;

export interface ContainerSize {
  width: number;
  height: number;
}

/**
 * CSS scale applied to the logical canvas, derived from the rendered container box.
 * The container is `aspect-ratio: 16/9`, so width and height agree; taking the minimum
 * keeps the gate correct if that ever changes.
 */
export function canvasScale({ width, height }: ContainerSize): number {
  if (width <= 0 || height <= 0) {
    return 0;
  }
  return Math.min(width / GAME_WIDTH, height / GAME_HEIGHT);
}

/** True when the rendered canvas keeps every interactive target at or above the AA floor. */
export function isViewportSupported(size: ContainerSize): boolean {
  return canvasScale(size) >= MIN_SUPPORTED_SCALE;
}

/** Rendered CSS size of a control that occupies `logicalPx` on the 1280x720 canvas. */
export function renderedTargetPx(size: ContainerSize, logicalPx: number): number {
  return canvasScale(size) * logicalPx;
}

interface GateElements {
  container: HTMLElement;
  gate: HTMLElement;
  title: HTMLElement;
  message: HTMLElement;
  action: HTMLButtonElement;
}

const PHONE_READY_SCENES = new Set<SceneKey>(
  SCENE_REGISTRY.filter((entry) => entry.phoneReady).map((entry) => entry.key),
);

interface ViewportGateRuntime {
  doc: Document;
  activeScene: SceneKey;
  blockedScene: SceneKey | null;
  sync: () => void;
  pending: { key: SceneKey; ready: () => void; cancel: () => void } | null;
  previousFocus: HTMLElement | null;
  reason: ViewportGateReason | null;
}

let runtime: ViewportGateRuntime | null = null;
let requestRevision = 0;
export type ViewportGateReason = 'orientation' | 'size' | 'scene';

export function viewportAccessRevision(): number { return requestRevision; }

function cancelPending(state: ViewportGateRuntime): void {
  const pending = state.pending;
  state.pending = null;
  pending?.cancel();
}

/** Only an explicit startup continuation subscribes; ordinary requests never acquire replay. */
export function whenSceneViewportAvailable(key: SceneKey, revision: number, ready: () => void, cancel: () => void): () => void {
  const state = runtime;
  if (!state || revision !== requestRevision || (state.blockedScene !== null && state.blockedScene !== key)) {
    cancel();
    return () => {};
  }
  cancelPending(state);
  const pending = { key, ready, cancel };
  state.pending = pending;
  state.sync();
  return () => { if (state.pending === pending) state.pending = null; };
}

function resolveElements(doc: Document): GateElements | null {
  const container = doc.getElementById('game-container');
  const gate = doc.getElementById('viewport-gate');
  const title = doc.getElementById('viewport-gate-title');
  const message = doc.getElementById('viewport-gate-message');
  const action = doc.getElementById('viewport-gate-action');
  return container && gate && title && message && action?.tagName === 'BUTTON'
    ? { container, gate, title, message, action: action as HTMLButtonElement }
    : null;
}

/**
 * While the gate is up, everything behind it is inert: not clickable, not focusable, and
 * absent from the accessibility tree.
 *
 * This covers the whole page rather than just `#game-container`, because focusable content
 * lives outside it — the legal nav, and `AuthScene`'s form, which mounts on `document.body`.
 * An overlay that merely paints over such controls leaves them reachable by keyboard.
 * `inert` also blurs any focused descendant, so focus cannot be stranded behind the gate.
 */
function setBackgroundInert(doc: Document, gate: HTMLElement, inert: boolean): void {
  for (const element of Array.from(doc.body.children)) {
    if (element === gate) {
      continue;
    }
    if (inert) {
      element.setAttribute('inert', '');
      element.setAttribute('aria-hidden', 'true');
    } else {
      element.removeAttribute('inert');
      element.removeAttribute('aria-hidden');
    }
  }
}

function applyGateState(state: ViewportGateRuntime, elements: GateElements, reason: ViewportGateReason | null): void {
  const { doc } = state;
  const { gate, title, message, action, container } = elements;
  const changed = state.reason !== reason || gate.hidden !== (reason === null);
  if (gate.hidden && reason !== null) {
    const focused = doc.activeElement;
    state.previousFocus = focused && 'focus' in focused ? focused as HTMLElement : null;
  }
  const focusWasInGate = gate.contains(doc.activeElement);
  gate.hidden = reason === null;
  setBackgroundInert(doc, gate, reason !== null);
  state.reason = reason;
  if (reason === null) {
    if (focusWasInGate) {
      if (state.previousFocus?.isConnected && !state.previousFocus.closest('[inert]')
        && !state.previousFocus.closest('[hidden]') && state.previousFocus.getClientRects().length > 0) {
        state.previousFocus.focus({ preventScroll: true });
      } else {
        container.tabIndex = -1;
        container.focus({ preventScroll: true });
      }
    }
    state.previousFocus = null;
    return;
  }
  gate.setAttribute('role', reason === 'scene' ? 'alertdialog' : 'alert');
  gate.setAttribute('aria-labelledby', title.id);
  if (reason === 'scene') gate.setAttribute('aria-modal', 'true');
  else gate.removeAttribute('aria-modal');
  title.textContent = reason === 'orientation' ? 'Rotate to play'
    : reason === 'scene' ? 'This screen needs a larger display' : 'Make the window larger';
  message.textContent = reason === 'orientation' ? 'Turn your phone sideways to continue.'
    : reason === 'scene' ? 'Go back, or use a larger display to open this screen.'
      : 'Enlarge the window or use a larger display.';
  action.textContent = 'Go back';
  action.hidden = reason !== 'scene';
  if (changed) {
    gate.tabIndex = -1;
    (action.hidden ? gate : action).focus({ preventScroll: true });
  }
}

export function sceneSupportsPhoneViewport(key: SceneKey): boolean {
  return PHONE_READY_SCENES.has(key);
}

/**
 * The real outer viewport size (visualViewport, falling back to innerWidth/innerHeight), used
 * for composition classification (`resolveResponsiveViewport`'s phone-landscape vs. standard
 * check). Falls back to the container box only when there is no `window` to read — e.g. a
 * detached `doc` in a test. The container itself is never an acceptable substitute for a real
 * window: it is aspect-locked to 16:9 by CSS, so a differently-shaped window (a tall or square
 * one) can produce a container box that happens to fall under the phone-landscape thresholds
 * even though the real viewport is nothing like a phone.
 */
function resolveOuterViewportSize(doc: Document, container: ContainerSize): ContainerSize {
  const view = doc.defaultView;
  return {
    width: view?.visualViewport?.width ?? view?.innerWidth ?? container.width,
    height: view?.visualViewport?.height ?? view?.innerHeight ?? container.height,
  };
}

/**
 * Whether `key` may be shown at this container/outer-viewport pair. The single source of truth
 * both `shouldBlockSceneAtViewport` (the pre-navigation check) and `installViewportGate`'s
 * `sync` (the ambient resize/orientation gate) call, so the two can never disagree about the
 * same real viewport — see the `resolveOuterViewportSize` doc comment for why `container` alone
 * cannot stand in for it.
 */
export function isSceneViewportSupported(key: SceneKey, container: ContainerSize, outerViewport: ContainerSize = container): boolean {
  return sceneViewportGateReason(key, container, outerViewport) === null;
}

export function sceneViewportGateReason(key: SceneKey, container: ContainerSize, outerViewport: ContainerSize = container): ViewportGateReason | null {
  if (isViewportSupported(container)) return null;
  const responsive = resolveResponsiveViewport(outerViewport, container);
  if (responsive.composition === 'unsupported-phone-portrait') return 'orientation';
  if (!isPhoneCompositionSupported(responsive)) return 'size';
  return sceneSupportsPhoneViewport(key) ? null : 'scene';
}

export function shouldBlockSceneAtViewport(key: SceneKey, container: ContainerSize, outerViewport?: ContainerSize): boolean {
  return !isSceneViewportSupported(key, container, outerViewport ?? container);
}

export function requestSceneViewportAccess(key: SceneKey): boolean {
  requestRevision += 1;
  if (runtime) cancelPending(runtime);
  if (!runtime) return true;
  const { doc } = runtime;
  const elements = resolveElements(doc);
  if (!elements) return true;
  const bounds = elements.container.getBoundingClientRect();
  const container = { width: bounds.width, height: bounds.height };
  const outerViewport = resolveOuterViewportSize(doc, container);
  if (shouldBlockSceneAtViewport(key, container, outerViewport)) {
    runtime.blockedScene = key;
    runtime.sync();
    return false;
  }
  runtime.activeScene = key;
  runtime.blockedScene = null;
  runtime.sync();
  return true;
}

/**
 * Installs the gate and keeps it in sync with the container box.
 * Returns a teardown function.
 */
export function installViewportGate(doc: Document = document): () => void {
  const elements = resolveElements(doc);
  if (!elements) {
    return () => {};
  }

  const state: ViewportGateRuntime = {
    doc,
    activeScene: SCENE_KEYS.Boot,
    blockedScene: null,
    sync: () => {},
    pending: null,
    previousFocus: null,
    reason: null,
  };
  const sync = () => {
    const { width, height } = elements.container.getBoundingClientRect();
    const container = { width, height };
    const outerViewport = resolveOuterViewportSize(doc, container);
    // CSS reads this to show page chrome (the legal nav) only on scenes where it covers no control.
    doc.body.dataset.scene = state.activeScene;
    if (state.blockedScene && isSceneViewportSupported(state.blockedScene, container, outerViewport)) {
      state.blockedScene = null;
    }
    elements.gate.dataset.blockedScene = state.blockedScene ?? '';
    applyGateState(state, elements, sceneViewportGateReason(state.blockedScene ?? state.activeScene, container, outerViewport));
    const pending = state.pending;
    if (pending && isSceneViewportSupported(pending.key, container, outerViewport)) {
      state.pending = null;
      pending.ready();
    }
  };
  state.sync = sync;
  runtime = state;

  const dismissBlockedScene = () => {
    requestRevision += 1;
    cancelPending(state);
    state.blockedScene = null;
    sync();
  };
  elements.action.addEventListener('click', dismissBlockedScene);

  sync();

  const view = doc.defaultView;
  const observer = view && typeof view.ResizeObserver === 'function' ? new view.ResizeObserver(sync) : null;
  observer?.observe(elements.container);
  const backgroundObserver = view && typeof view.MutationObserver === 'function' ? new view.MutationObserver(() => {
    if (state.reason !== null) setBackgroundInert(doc, elements.gate, true);
  }) : null;
  backgroundObserver?.observe(doc.body, { childList: true });
  view?.addEventListener('resize', sync);
  view?.addEventListener('orientationchange', sync);
  view?.visualViewport?.addEventListener('resize', sync);
  return () => {
    observer?.disconnect();
    backgroundObserver?.disconnect();
    view?.removeEventListener('resize', sync);
    view?.removeEventListener('orientationchange', sync);
    view?.visualViewport?.removeEventListener('resize', sync);
    elements.action.removeEventListener('click', dismissBlockedScene);
    cancelPending(state);
    if (runtime === state) runtime = null;
  };
}

/**
 * Gives the Phaser-created canvas a name and a role.
 *
 * The canvas has no accessible children — Phaser draws its UI as pixels — so it is
 * announced as a single labelled image rather than pretending to be an application
 * with a focusable widget tree. Interactive screens that need real semantics build
 * DOM controls instead (see AuthScene).
 */
export function labelGameCanvas(canvas: HTMLCanvasElement, label: string): void {
  canvas.setAttribute('role', 'img');
  canvas.setAttribute('aria-label', label);
}
